import { useParams } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { useOgMeta, buildOgImageUrl } from '@/hooks/use-og-meta';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RecommendationBadge } from '@/components/recommendation-badge';
import { ConfidenceBar } from '@/components/ui/confidence-bar';
import { PlatformBadge } from '@/components/platform-badge';
import { Spinner } from '@/components/ui/spinner';
import { SignInButton } from '@clerk/react';
import { Link } from 'wouter';
import {
  Brain, Target, AlertTriangle, ArrowLeft, ChevronDown, ArrowRight,
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { RiskBadge } from '@/components/risk-badge';

interface PublicAnalysis {
  id: number;
  marketQuestion: string;
  platform: 'polymarket' | 'kalshi';
  recommendation: 'YES' | 'NO';
  confidence: number;
  riskLevel: 'Low' | 'Medium' | 'High' | 'Extreme';
  edgeSummary: string;
  reasoning: string | null;
  yesPrice: number | null;
  noPrice: number | null;
  targetPrice: number | null;
  createdAt: string;
}

export default function AnalysisPublicPage() {
  const { token } = useParams<{ token: string }>();
  const [isReasoningOpen, setIsReasoningOpen] = useState(false);

  const { data: analysis, isLoading, isError } = useQuery<PublicAnalysis>({
    queryKey: ['public-analysis', token],
    queryFn: async () => {
      const res = await fetch(`/api/analyses/public/${token}`);
      if (!res.ok) throw new Error('Not found');
      return res.json();
    },
    enabled: !!token,
    retry: false,
  });

  // ─── Dynamic OG meta tags for shared analysis links ───────────────────────
  useOgMeta({
    title: analysis?.marketQuestion,
    description: analysis?.edgeSummary
      ? `${analysis.recommendation} · ${analysis.edgeSummary}`
      : analysis?.marketQuestion,
    imageUrl: analysis
      ? buildOgImageUrl({
          title: analysis.marketQuestion,
          probability: analysis.yesPrice ?? undefined,
          platform: analysis.platform,
          summary: analysis.edgeSummary,
          recommendation: analysis.recommendation,
        })
      : undefined,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      {/* Top bar */}
      <div className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center">
              <Brain className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold">MarketMind</span>
          </div>
          <SignInButton mode="modal">
            <Button size="sm">
              Get Started Free <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          </SignInButton>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : isError || !analysis ? (
          <Card>
            <CardContent className="py-16 text-center">
              <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Analysis not found</h2>
              <p className="text-muted-foreground mb-6">
                This link may have expired or the analysis is no longer public.
              </p>
              <Link href="/">
                <Button variant="outline">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Go to MarketMind
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Header */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <PlatformBadge platform={analysis.platform} />
                <span className="text-sm text-muted-foreground">
                  {new Date(analysis.createdAt).toLocaleDateString('en-US', {
                    month: 'long', day: 'numeric', year: 'numeric',
                  })}
                </span>
              </div>
              <h1 className="text-2xl font-bold">{analysis.marketQuestion}</h1>
              {(analysis.yesPrice != null || analysis.noPrice != null) && (
                <div className="flex gap-4 mt-3 text-sm text-muted-foreground">
                  {analysis.yesPrice != null && (
                    <span>YES at time of analysis: <strong className="text-foreground">{Math.round(analysis.yesPrice * 100)}¢</strong></span>
                  )}
                  {analysis.noPrice != null && (
                    <span>NO: <strong className="text-foreground">{Math.round(analysis.noPrice * 100)}¢</strong></span>
                  )}
                </div>
              )}
            </div>

            {/* Analysis card */}
            <Card className="border-2 border-primary/20">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>AI Analysis</CardTitle>
                  <RecommendationBadge recommendation={analysis.recommendation} size="lg" />
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div><ConfidenceBar confidence={analysis.confidence} /></div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Risk Level</span>
                    <RiskBadge risk={analysis.riskLevel as any} />
                  </div>
                </div>

                {analysis.edgeSummary && (
                  <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      Edge Summary
                    </h4>
                    <p className="text-sm">{analysis.edgeSummary}</p>
                  </div>
                )}

                {analysis.reasoning && (
                  <Collapsible open={isReasoningOpen} onOpenChange={setIsReasoningOpen}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full p-4 rounded-lg hover:bg-muted transition-colors">
                      <h4 className="font-semibold">Full Reasoning</h4>
                      <ChevronDown className={cn('h-5 w-5 transition-transform', isReasoningOpen && 'rotate-180')} />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-4">
                      <p className="text-sm whitespace-pre-wrap">{analysis.reasoning}</p>
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {analysis.targetPrice && (
                  <div>
                    <h4 className="font-semibold mb-3">Target</h4>
                    <div className="p-4 rounded-lg border border-border inline-block">
                      <div className="text-xs text-muted-foreground mb-1">Cash out at</div>
                      <div className="text-xl font-bold text-emerald-600">
                        {Math.round(analysis.targetPrice * 100)}¢
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-2 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-amber-900">⚠️ Not financial advice. Educational purposes only. Always do your own research.</p>
                </div>
              </CardContent>
            </Card>

            {/* CTA */}
            <Card className="bg-gradient-to-br from-primary/5 to-purple-500/5 border-primary/20">
              <CardContent className="p-6 text-center">
                <Brain className="h-10 w-10 text-primary mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-2">Run your own AI analyses</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  MarketMind uses Claude to analyze any Polymarket or Kalshi market in seconds. 5 free analyses per month.
                </p>
                <SignInButton mode="modal">
                  <Button size="lg" className="w-full sm:w-auto">
                    Get Started Free <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </SignInButton>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
